const actor = (name) => ({ type: "actor", value: name });
const text = (value) => ({ type: "text", value });
const link = (value, resourceType, resourceId) => ({
  type: "link",
  value,
  resourceType,
  resourceId,
});

const partLabel = (metadata) =>
  metadata.partNumber
    ? metadata.partName
      ? `${metadata.partNumber} (${metadata.partName})`
      : metadata.partNumber
    : metadata.partName || `#${metadata.partId}`;

const fieldLabels = {
  manager: "Manager",
  status: "Status",
  material: "Metal",
  price: "Price",
  quote: "Quote",
};

export const renderNotification = ({
  eventType,
  metadata,
  actorName,
  resourceType,
}) => {
  switch (eventType) {
    // ── Quotes ──────────────────────────────────────────────────────────
    case "quote.created":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" created quote "),
          link(metadata.quoteNumber, "quote", metadata.quoteId),
          ...(metadata.customerName
            ? [
                text(" for "),
                link(metadata.customerName, "customer", metadata.customerId),
              ]
            : []),
        ],
      };
    case "quote.stage_changed":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" moved "),
          link(metadata.quoteNumber, "quote", metadata.quoteId),
          text(` to ${metadata.toLabel}`),
        ],
      };
    case "quote.assigned":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" assigned you to "),
          link(metadata.quoteNumber, "quote", metadata.quoteId),
        ],
      };

    // ── Comments ────────────────────────────────────────────────────────
    case "comment.mention":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" mentioned you on "),
          link(
            metadata.resourceLabel,
            metadata.resourceType,
            metadata.resourceId,
          ),
        ],
        preview: metadata.bodyPreview,
      };
    case "comment.reply":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" replied to your comment on "),
          link(
            metadata.resourceLabel,
            metadata.resourceType,
            metadata.resourceId,
          ),
        ],
        preview: metadata.bodyPreview,
      };
    case "comment.reaction":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(` reacted ${metadata.emoji} to your comment`),
        ],
      };

    // ── Work Orders ─────────────────────────────────────────────────────
    case "work_order.created": {
      const count = metadata.count ?? 1;
      const numbers = metadata.workOrderNumbers ?? [];
      if (count === 1) {
        return {
          resourceType,
          parts: [
            actor(actorName),
            text(" created work order "),
            link(
              numbers[0] || metadata.workOrder,
              "work_order",
              metadata.workOrderIds?.[0] ?? metadata.workOrderId,
            ),
            ...(metadata.partId
              ? [
                  text(" for "),
                  link(partLabel(metadata), "part", metadata.partId),
                ]
              : []),
          ],
        };
      }
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(` created ${count} work orders for `),
          link(partLabel(metadata), "part", metadata.partId),
        ],
        preview:
          numbers.slice(0, 6).join(", ") + (numbers.length > 6 ? "…" : ""),
      };
    }
    case "work_order.completed":
      if (metadata?.closedByAdmin) {
        return {
          resourceType,
          parts: [
            actor(actorName),
            text(" closed work order "),
            link(metadata.workOrder, "work_order", metadata.workOrderId),
          ],
        };
      }
      return {
        resourceType,
        parts: [
          text("Work order "),
          link(metadata.workOrder, "work_order", metadata.workOrderId),
          text(" has been completed"),
        ],
      };
    case "work_order.deleted":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" deleted work order "),
          link(metadata.workOrder, "work_order", metadata.workOrderId),
        ],
      };
    case "work_order.due_date_changed":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" changed the due date on "),
          link(metadata.workOrder, "work_order", metadata.workOrderId),
          text(
            ` from ${metadata.fromDueDate ?? "—"} to ${metadata.toDueDate ?? "—"}`,
          ),
        ],
      };
    case "work_order.arrived_at":
      return {
        resourceType,
        parts: [
          link(metadata.workOrder, "work_order", metadata.workOrderId),
          text(" arrived at "),
          {
            type: "text",
            value: metadata.stationLabel || "watched station",
            emphasized: true,
          },
        ],
      };

    // ── Parts ───────────────────────────────────────────────────────────
    case "part.created":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" created part "),
          link(partLabel(metadata), "part", metadata.partId),
        ],
      };
    case "part.deleted":
      return {
        resourceType,
        parts: [actor(actorName), text(` deleted part ${partLabel(metadata)}`)],
      };
    case "part.updated":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" updated "),
          link(metadata.partName, "part", metadata.partId),
          ...(metadata.changedFields?.length
            ? [text(` — ${metadata.changedFields.join(", ")}`)]
            : []),
        ],
      };
    case "part.details_changed": {
      const fields = (metadata.changes || []).map(
        (c) => fieldLabels[c.field] || c.field,
      );
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" updated "),
          link(partLabel(metadata), "part", metadata.partId),
          ...(fields.length ? [text(`: ${fields.join(", ")}`)] : []),
        ],
        preview: (metadata.changes || [])
          .map((c) => {
            const label = fieldLabels[c.field] || c.field;
            return `${label}: ${c.from ?? "—"} → ${c.to ?? "—"}`;
          })
          .join(" · "),
      };
    }

    // ── Orders ──────────────────────────────────────────────────────────
    case "order.created":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" created order "),
          link(
            metadata.poNumber || `#${metadata.orderId}`,
            "order",
            metadata.orderId,
          ),
          ...(metadata?.customer?.id && metadata?.customer?.name
            ? [
                text(` for `),
                link(
                  metadata?.customer?.name,
                  "customer",
                  metadata?.customer?.id,
                ),
              ]
            : []),
        ],
      };
    case "order.deleted":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(` deleted order ${metadata.poNumber || `#${metadata.orderId}`}`),
        ],
      };
    case "order.due_date_changed":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" changed the due date on "),
          link(
            metadata.poNumber || `#${metadata.orderId}`,
            "order",
            metadata.orderId,
          ),
          text(
            ` from ${metadata.fromDueDate ?? "—"} to ${metadata.toDueDate ?? "—"}`,
          ),
        ],
      };

    // ── Shipments ───────────────────────────────────────────────────────
    case "shipment.created":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(" created shipment "),
          link(
            metadata.shipmentNumber || `#${metadata.shipmentId}`,
            "shipment",
            metadata.shipmentId,
          ),
        ],
      };
    case "shipment.deleted":
      return {
        resourceType,
        parts: [
          actor(actorName),
          text(
            ` deleted shipment ${metadata.shipmentNumber || `#${metadata.shipmentId}`}`,
          ),
        ],
      };
    case "shipment.shipped":
      return {
        resourceType,
        parts: [
          text("Shipment "),
          link(
            metadata.shipmentNumber || `#${metadata.shipmentId}`,
            "shipment",
            metadata.shipmentId,
          ),
          text(" shipped"),
        ],
      };
    case "shipment.completed":
      return {
        resourceType,
        parts: [text("Shipment has been completed")],
      };

    default:
      return {
        resourceType,
        parts: [{ type: "text", value: "Something was updated" }],
      };
  }
};
